import AtomContainer from "../lib/AtomContainer"
import IAtomModel from "../lib/IAtomModel";
import {getAllRings, getSortedGraph, getSpanningTree, removeOverlapRings} from "./RingCalculations";
import IBondModel from "../lib/IBondModel";
import {AtomContainerQuery} from "../lib/AtomContainerQuery";

export const getRings = (atoms: IAtomModel[],container: AtomContainer) => {
    const graph = getSortedGraph(atoms.slice(), container);
    const spanningTree = getSpanningTree(graph, container);
    const rings = getAllRings(graph, spanningTree, container, false);
    removeOverlapRings(container, rings);

    return rings;
}

export const breakRings = (atoms: IAtomModel[],container: AtomContainer) => {
    const graph = getSortedGraph(atoms.slice(), container);
    const spanningTree = getSpanningTree(graph, container);
    getAllRings(graph, spanningTree, container, true);
}

export const getAllRingAtoms = (atoms: IAtomModel[],container: AtomContainer) => {
    const rings = getRings(atoms, container);
    // can't simply concat or flat because there may be repeat atoms in different rings
    return rings.reduce((acc,curr) => {
        curr.forEach(a => {
            if(!acc.includes(a)) acc.push(a);
        })
        return acc;
    },[]);
}

export const getSharedRings = (a1: IAtomModel, a2: IAtomModel, container: AtomContainer, rings?: IAtomModel[][]): IAtomModel[][] | undefined => {
    if(!rings) rings = getRings(container.atoms, container);

    const sharedRings = rings.filter(r => r.includes(a1) && r.includes(a2));

    if(sharedRings.length === 0) return undefined;

    return sharedRings;
}

export const getAtomRings = (a1: IAtomModel, container: AtomContainer, rings?: IAtomModel[][]) => {
    if(!rings) rings = getRings(container.atoms, container);

    const atomRings = rings.filter(r => r.includes(a1));

    if(atomRings.length === 0) return undefined;

    return atomRings;
}

export const isInRing = (atom: IAtomModel, container: AtomContainer, rings?: IAtomModel[][]) => {
    if(!rings) rings = getRings(container.atoms, container);

    const atomIsInRing = rings.some(ring => ring.includes(atom));
    return atomIsInRing;
}

export const getRingBonds = (container: AtomContainer, rings?: IAtomModel[][]) => {
    if(!rings) rings = getRings(container.atoms, container);

    const ringBonds = rings.map(ring => {
        const currRingBonds: IBondModel[] = [];
        // get the bond between the curr atom and the one next to it
        for(let i = 0; i < ring.length; i++) {
            const a1 = ring[i];
            let a2: IAtomModel;
            if(i === ring.length - 1) a2 = ring[0];
            else a2 = ring[i+1];
            const sharedBond = AtomContainerQuery.getBondBetween(container, a1, a2);

            if(sharedBond != undefined) currRingBonds.push(sharedBond);
        }

        return currRingBonds;
    })

    return ringBonds;
}

export const getAdjacentAtoms = (ring: IAtomModel[], atom: IAtomModel)=> {
    if(ring.length < 3) return undefined;
    const index = ring.indexOf(atom);
    if(index < 0) return undefined;

    if(index === 0) return [ring[ring.length - 1], ring[1]];
    if(index === ring.length - 1) return [ring[index-1], ring[0]];

    return [ring[index-1], ring[index+1]];
}

// -1 is n or less connections, 0 is exactly n connections, 1 is n or more connections
export const getConnectedRings = (ring: IAtomModel[], allRings: IAtomModel[][], n = 1, threshold: -1 | 0 | 1 = 1) => {
    if(n <= 0) n = 1;
    const connectedRings = allRings.filter(currRing =>{
        const connections = currRing.filter(a => ring.includes(a)).length;
        const sameRing = currRing.length === ring .length && currRing.every(a => ring.includes(a));
        if(!sameRing && connections > 0 && meetsThreshold(n, connections, threshold)) return true;
        return false;
    });
    if(connectedRings.length === 0) return undefined;

    return connectedRings;
}
const meetsThreshold = (expected: number, actual: number, threshold: -1 | 0| 1) => {
    if(expected - actual === 0) return true;
    else if(threshold !== 0) return (actual - expected)/Math.abs(actual - expected) === threshold;
    return false;
}

export const getSharedAtoms = (ring1: IAtomModel[], ring2: IAtomModel[]) => {
    const sharedAtoms = ring1.filter(a => ring2.includes(a));
    if(sharedAtoms.length === 0) return undefined;
    return sharedAtoms;
}


